import React from "react";
import PropTypes from "prop-types";

function parseLine(line) {
  const [beforeColon, afterColon] = line.split(":", 2);
  const author = (beforeColon || "").trim();
  return {
    author: author ? author.charAt(0).toUpperCase() + author.slice(1) : "",
    text: afterColon ? afterColon.trim() : "",
  };
}

function NoteDialog({ open2, handleClose2, note, note2, setNote, handleChangeNote, user, handleDeleteNote }) {
  if (!open2) return null;

  const allLines = note ? note.split("\n") : [];
  const hiddenCount = Math.max(0, allLines.length - 10);
  const visibleLines = allLines.slice(-10);

  return (
    <div style={styles.overlay} onClick={handleClose2}>
      <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Notes</h2>
          <button type="button" style={styles.closeButton} onClick={handleClose2} aria-label="Close">
            ×
          </button>
        </div>

        <div style={styles.content}>
          {allLines.length ? (
            <div style={styles.list}>
              {hiddenCount > 0 ? (
                <div style={styles.more}>{hiddenCount} earlier notes hidden</div>
              ) : null}
              {visibleLines.map((line, index) => {
                const originalIndex = hiddenCount + index;
                const { author, text } = parseLine(line);
                const isUser = author.toLowerCase() === (user || "").toLowerCase();

                return (
                  <div key={`${author}-${originalIndex}`} style={styles.item}>
                    <div style={styles.itemHead}>
                      <div style={styles.authorRow}>
                        <span style={styles.avatar}>{author ? author.charAt(0) : "N"}</span>
                        <span style={styles.author}>{author || "Note"}</span>
                      </div>
                      {isUser ? (
                        <button type="button" style={styles.deleteButton} onClick={() => handleDeleteNote(originalIndex)}>
                          Remove
                        </button>
                      ) : null}
                    </div>
                    {text ? <div style={styles.itemBody}>{text}</div> : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.empty}>No notes yet</div>
          )}

          <textarea
            style={styles.textarea}
            value={note2}
            rows={3}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Write a note"
          />
        </div>

        <div style={styles.actions}>
          <button type="button" onClick={handleClose2} style={styles.cancelButton}>
            Cancel
          </button>
          <button type="button" onClick={handleChangeNote} style={styles.updateButton}>
            Add note
          </button>
        </div>
      </div>
    </div>
  );
}

NoteDialog.propTypes = {
  open2: PropTypes.bool.isRequired,
  handleClose2: PropTypes.func.isRequired,
  note: PropTypes.string,
  note2: PropTypes.string,
  user: PropTypes.string.isRequired,
  setNote: PropTypes.func,
  handleChangeNote: PropTypes.func,
  handleDeleteNote: PropTypes.func,
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15, 23, 42, 0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1300,
    padding: 16,
  },
  dialog: {
    backgroundColor: "#fff",
    borderRadius: 16,
    width: 480,
    maxWidth: "100%",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
    fontFamily: 'Inter, "Public Sans", Roboto, Helvetica, Arial, sans-serif',
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
  },
  title: {
    margin: 0,
    color: "#1e293b",
    fontSize: 18,
    fontWeight: 600,
  },
  closeButton: {
    width: 32,
    height: 32,
    border: "none",
    borderRadius: 8,
    background: "transparent",
    color: "#64748b",
    fontSize: 22,
    lineHeight: 1,
    cursor: "pointer",
  },
  content: {
    padding: 20,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginBottom: 16,
    maxHeight: 280,
    overflowY: "auto",
  },
  item: {
    padding: "10px 12px",
    background: "#fff8f1",
    border: "1px solid #fde8c8",
    borderRadius: 10,
  },
  itemHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 6,
  },
  authorRow: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    minWidth: 0,
  },
  avatar: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "#fdba74",
    color: "#7c2d12",
    fontSize: 11,
    fontWeight: 700,
    flexShrink: 0,
  },
  author: {
    color: "#9a3412",
    fontSize: 12,
    fontWeight: 600,
  },
  deleteButton: {
    border: "none",
    background: "transparent",
    color: "#94a3b8",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    padding: 0,
  },
  itemBody: {
    color: "#1e293b",
    fontSize: 13,
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  more: {
    color: "#94a3b8",
    fontSize: 12,
  },
  empty: {
    marginBottom: 16,
    color: "#94a3b8",
    fontSize: 13,
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #fde8c8",
    background: "#fff8f1",
    color: "#1e293b",
    fontSize: 13,
    fontFamily: "inherit",
    boxSizing: "border-box",
    resize: "none",
    outline: "none",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    padding: "0 20px 18px",
  },
  cancelButton: {
    padding: "8px 14px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    background: "#fff",
    color: "#475569",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
  },
  updateButton: {
    padding: "8px 14px",
    border: "none",
    borderRadius: 8,
    background: "#4d7cfe",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default NoteDialog;
